import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Github, Linkedin, Mail, Gitlab } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import emailjs from '@emailjs/browser';

// ─── CONFIG EMAILJS ───────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_3osj92s';
const EMAILJS_TEMPLATE_ID = 'template_9xa1wsw';
const EMAILJS_PUBLIC_KEY  = 'ILAUdgwTGGQYYgT4K';
// ──────────────────────────────────────────────────────────────────

const getBrowser = (ua) => {
  if (/Edg\//.test(ua))        return 'Microsoft Edge';
  if (/OPR\/|Opera/.test(ua))  return 'Opera';
  if (/Chrome\//.test(ua))     return 'Google Chrome';
  if (/Firefox\//.test(ua))    return 'Mozilla Firefox';
  if (/Safari\//.test(ua))     return 'Apple Safari';
  if (/Trident\//.test(ua))    return 'Internet Explorer';
  return 'Navigateur inconnu';
};

const getOS = (ua) => {
  if (/Windows NT 10/.test(ua))  return 'Windows 10/11';
  if (/Windows NT 6.3/.test(ua)) return 'Windows 8.1';
  if (/Windows NT 6.1/.test(ua)) return 'Windows 7';
  if (/Mac OS X/.test(ua))       return 'macOS';
  if (/Android/.test(ua))        return 'Android';
  if (/iPhone|iPad/.test(ua))    return 'iOS';
  if (/Linux/.test(ua))          return 'Linux';
  return 'OS inconnu';
};

const Hero = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  useEffect(() => {
    const STORAGE_KEY = 'portfolio_visited';

    if (localStorage.getItem(STORAGE_KEY)) return;

    const sendVisitorNotification = async () => {
      try {
        const ipRes  = await fetch('https://api.ipapi.is/');
        const ipData = await ipRes.json();

        const ua       = navigator.userAgent;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const templateParams = {
          visitor_ip      : ipData.ip                 || 'N/A',
          visitor_country : ipData.location?.country  || 'N/A',
          visitor_city    : ipData.location?.city      || 'N/A',
          visitor_isp     : ipData.asn?.org            || 'N/A',
          visitor_browser : getBrowser(ua),
          visitor_os      : getOS(ua),
          visitor_language: navigator.language         || 'N/A',
          visitor_screen  : `${window.screen.width}x${window.screen.height}`,
          visitor_timezone: timezone,
          visitor_time    : new Date().toLocaleString('fr-FR', { timeZone: timezone }),
          visitor_referrer: document.referrer          || 'Accès direct',
          

        };

        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          {
          to_name:'Stéphan',
          to_email: 'rabemananjarafalimananastephan@gmail.com',
          message:JSON.stringify(
            templateParams
          )
          },
          EMAILJS_PUBLIC_KEY
        );

        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (err) {
        console.warn('Notification visiteur échouée :', err);
      }
    };

    sendVisitorNotification();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { delayChildren: 0.3, staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  const socialLinks = [
    { icon: Github,   href: 'https://github.com/falimananaGithub',                         label: 'GitHub'   },
    { icon: Gitlab,   href: 'https://gitlab.com/rabemananjarafalimananastephan',            label: 'GitLab'   },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/stéphan-rabemananjara-b297b5310/',label: 'LinkedIn' },
    { icon: Mail,     href: 'mailto:rabemananjarafalimananastephan@gmail.com',              label: 'Email'    }
  ];

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden">

      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-blue-600/20" />

      <motion.div
        ref={ref}
        className="container mx-auto px-6 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      >
        <div className="text-center">

          {/* Titre */}
          <motion.div variants={itemVariants}>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              Bonjour, je suis
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400">
                Développeur Web
              </span>
            </h1>
          </motion.div>

          {/* Sous-titre */}
          <motion.p
            variants={itemVariants}
            className="text-xl md:text-2xl text-gray-200 mb-8 max-w-2xl mx-auto"
          >
            Passionné par la création d'applications web, mobile modernes et performantes,
            je transforme vos idées en expériences numériques exceptionnelles.
          </motion.p>

          {/* Boutons CTA */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <motion.a
              href="#contact"
              className="btn bg-white text-purple-600 hover:bg-gray-100"
            >
              Me contacter
            </motion.a>
            <motion.a
              href="#projects"
              className="btn btn-outline border-white text-white hover:bg-white hover:text-purple-600"
            >
              Voir mes projets
            </motion.a>
          </motion.div>

          {/* Réseaux sociaux */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center gap-6 mb-12"
          >
            {socialLinks.map((link, index) => (
              <motion.a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white hover:text-purple-600 transition-all duration-300"
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <link.icon size={20} />
              </motion.a>
            ))}
          </motion.div>

          {/* Flèche scroll */}
          <motion.div variants={itemVariants} className="flex justify-center">
            <motion.a
              href="#about"
              className="text-white animate-bounce"
              whileHover={{ scale: 1.2 }}
            >
              <ArrowDown size={30} />
            </motion.a>
          </motion.div>

        </div>
      </motion.div>

      {/* Indicateur scroll souris */}
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white rounded-full mt-2" />
        </div>
      </motion.div>

    </section>
  );
};

export default Hero;